<template>
  <div class="bg-white shadow-md rounded-lg p-6 h-full">
    <h2 class="text-xl font-semibold text-gray-800 border-b pb-4">Invite New Guest</h2>
    <div v-if="!generatedLink">
      <form @submit.prevent="createInvite" class="mt-4 space-y-4">
        <div><label for="guestName" class="block text-sm font-medium text-gray-700">Guest Full Name</label><input v-model="form.guestName" type="text" id="guestName" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-purple focus:border-brand-purple"></div>
        <div><label for="guestEmail" class="block text-sm font-medium text-gray-700">Guest Email</label><input v-model="form.guestEmail" type="email" id="guestEmail" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-purple focus:border-brand-purple"></div>
        <div><label for="podcastSubject" class="block text-sm font-medium text-gray-700">Podcast Subject / Title (Optional)</label><input v-model="form.podcastSubject" type="text" id="podcastSubject" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-purple focus:border-brand-purple"></div>
        <div><label for="recordingDate" class="block text-sm font-medium text-gray-700">Recording Date & Time (Optional)</label><input v-model="form.recordingDate" type="datetime-local" id="recordingDate" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-purple focus:border-brand-purple"></div>
        <div><label for="recordingLink" class="block text-sm font-medium text-gray-700">Recording Software Link (Optional)</label><input v-model="form.recordingLink" type="text" id="recordingLink" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-purple focus:border-brand-purple" placeholder="Paste recording link here..."></div>
        <!-- START: CORRECTED BUTTON COLOUR -->
        <div class="pt-2"><button type="submit" :disabled="isProcessing" class="w-full bg-brand-purple-dark text-white py-2 px-4 rounded-md hover:opacity-90 disabled:opacity-50"><span v-if="isProcessing">Creating Invite...</span><span v-else>Create Invite</span></button></div>
        <!-- END: CORRECTED BUTTON COLOUR -->
      </form>
    </div>
    <div v-if="generatedLink" class="mt-4">
      <div class="p-4 rounded-md bg-green-50 border border-green-200"><p class="font-semibold text-green-800">✅ Success! Your guest invitation is ready.</p><p class="text-sm text-green-700 mt-1">Copy the email template below and send it to your guest.</p></div>
      <div class="mt-4 p-4 border rounded-lg bg-gray-50"><h3 class="font-semibold text-gray-800">Email Template for {{ form.guestName }}</h3><div class="mt-2 p-3 bg-white border rounded-md text-sm text-gray-600 whitespace-pre-wrap">{{ emailBody }}</div></div>
      <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button @click="copyEmail" :disabled="isCopied" class="w-full text-white py-2 px-4 rounded-md transition-colors" :class="isCopied ? 'bg-green-500' : 'bg-brand-purple-dark hover:opacity-90'"><span v-if="isCopied">Copied! ✅</span><span v-else>Copy Email to Clipboard</span></button>
        <button @click="resetForm" class="w-full bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700">Create Another Invite</button>
      </div>
    </div>
    <div v-if="feedbackMessage" class="mt-4 p-3 rounded-md text-sm" :class="isError ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'"><p>{{ feedbackMessage }}</p></div>
  </div>
</template>

<script>
import { db } from '@/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'InviteGuestPanel',
  data() {
    return {
      form: { guestName: '', guestEmail: '', podcastSubject: '', recordingDate: '', recordingLink: '' },
      isProcessing: false, feedbackMessage: '', isError: false,
      generatedLink: null, isCopied: false,
    }
  },
  computed: {
    ...mapState(['clientId', 'clientDetails', 'user']),
    ...mapGetters(['displayName']),
    emailBody() {
      const podcastName = this.clientDetails?.podcastName || 'our podcast';
      let scheduleSection = "The recording time will be confirmed in a separate email shortly.";
      if (this.form.recordingDate) {
        const date = new Date(this.form.recordingDate);
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' };
        const formattedDate = new Intl.DateTimeFormat('en-GB', options).format(date);
        scheduleSection = `The recording is scheduled for:\n${formattedDate}`;
      }
      let subjectSection = `We're really excited to chat with you and share your insights with our audience.`;
      if (this.form.podcastSubject) {
        subjectSection = `We're really excited to chat with you about "${this.form.podcastSubject}" and share your insights with our audience.`;
      }
      return `Subject: 🎙️ Podcast Invitation: ${podcastName}\n\nHi ${this.form.guestName},\n\nWe're thrilled to invite you to be a guest on ${podcastName}! ${subjectSection}\n\n${scheduleSection}\n\nTo help us get everything ready, please follow the single link below. There, you can sign the mandatory release agreement and view a quick pre-recording checklist to ensure everything runs smoothly.\n\nHere is your unique link:\n${this.generatedLink}\n\nWe can't wait to record with you!\n\nKind regards,\nThe ${podcastName} Team`;
    },
  },
  methods: {
    async createInvite() {
      this.isProcessing = true; this.feedbackMessage = ''; this.isError = false;
      if (!this.clientId || !this.user) {
        this.feedbackMessage = "Error: Could not identify your client or user. Please log in again.";
        this.isError = true; this.isProcessing = false; return;
      }
      try {
        const submissionData = {
          clientId: this.clientId,
          guestName: this.form.guestName,
          guestEmail: this.form.guestEmail,
          podcastSubject: this.form.podcastSubject || null,
          recordingDate: this.form.recordingDate ? new Date(this.form.recordingDate) : null,
          recordingLink: this.form.recordingLink || null,
          status: 'pending_guest_signature',
          createdAt: serverTimestamp(),
          createdByUid: this.user.uid,
          createdByName: this.displayName,
        };
        const docRef = await addDoc(collection(db, 'submissions'), submissionData);
        this.generatedLink = `${window.location.origin}/guest-guide/${docRef.id}`;
      } catch (error) {
        console.error("Error creating submission: ", error);
        this.feedbackMessage = "An unexpected error occurred."; this.isError = true;
      } finally {
        this.isProcessing = false;
      }
    },
    async copyEmail() {
      if (this.isCopied) return;
      try {
        await navigator.clipboard.writeText(this.emailBody);
        this.isCopied = true;
        setTimeout(() => { this.isCopied = false; }, 2500);
      } catch (err) {
        this.feedbackMessage = 'Failed to copy email. Please copy it manually.';
        this.isError = true;
      }
    },
    resetForm() {
      this.form = { guestName: '', guestEmail: '', podcastSubject: '', recordingDate: '', recordingLink: '' };
      this.feedbackMessage = ''; this.isError = false; this.generatedLink = null; this.isCopied = false;
    }
  }
}
</script>